<template>
    <div class="flex items-center gap-4 flex-wrap m-8">
        <Skeletor v-if="!paymentMethods" />
        <template v-else>
            <div
                v-for="(paymentMethod, index) in paymentMethods"
                :key="index"
                class="text-xl font-bold tracking-widest cursor-pointer"
                :id="'payment-tab-' + paymentMethod.id"
            >
                {{ paymentMethod.name }}
            </div>
        </template>
    </div>
    <div class="m-8 mb-0">
        <Skeletor v-if="!unpayedOrders" height="50vh" />
        <template v-else>
            <table v-for="(paymentMethod, index) in paymentMethods" :key="index" class="w-full" :id="'payment-content-' + paymentMethod.id">
                <thead>
                    <tr class="text-left uppercase text-sm text-white bg-gray-dark">
                        <th class="p-4">KW</th>
                        <th class="p-4">Kunde</th>
                        <th class="p-4">Zahlunsart</th>
                        <th class="p-4">Bezahlt</th>
                        <th class="p-4">Summe</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="unpayedOrders.filter(order => paymentMethod.id == 'all' || order.customer.payment_method.id == paymentMethod.id).length == 0">
                        <td colspan="6" class="p-4 text-center">Keine offenen Zahlungen gefunden.</td>
                    </tr>
                    <template v-else>
                        <tr v-for="(order, index) in unpayedOrders.filter(order => paymentMethod.id == 'all' || order.customer.payment_method.id == paymentMethod.id)" :key="index" :class="index % 2 == 0 ? 'bg-gray-lighter' : 'bg-white'" class="h-20">
                            <td>
                                <div class="mx-1 sm:mx-4 whitespace-nowrap">{{ order.week }}</div>
                            </td>
                            <td class="w-full max-w-0">
                                <div class="mx-1 sm:mx-4">
                                    <div v-html="order.customer.name" />
                                    <span class="text-sm" v-html="order.customer.address" /> / <span class="text-sm" v-html="order.customer.tour.name" />
                                </div>
                            </td>
                            <td>
                                <div class="mx-1 sm:mx-4 flex flex-col">
                                    {{ order.customer.payment_method.name }}
                                </div>
                            </td>
                            <td>
                                <div class="mx-1 sm:mx-4 flex flex-col text-center">
                                    <checkbox-input label="" v-model:input="order.is_payed" @change="setOrdersPaymentStatus(order.order_ids, order.is_payed)" />
                                </div>
                            </td>
                            <td>
                                <div class="mx-1 sm:mx-4 flex flex-col text-right">
                                    {{ currency.format(
                                            order.ordermeals.reduce(
                                                (sum, om) => sum + om.quantity * om.meal.price,
                                                0
                                            )
                                        ) }}
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <button
                class="print:hidden font-montserrat text-xs block w-full bg-purple hover:bg-black text-white py-3 transition-colors duration-300 uppercase tracking-widest font-bold"
                type="submit"
                @click="printPage"
            >
                <font-awesome-icon icon="fa-solid fa-print" />
                &nbsp;
                <slot>Drucken</slot>
            </button>
        </template>
    </div>
</template>

<script setup>
import { onMounted, inject, ref, nextTick } from "vue";
import { useTitleStore } from "@/store/title-store";
import { useToast } from "vue-toast-notification";
import { Tabs } from "flowbite";

import CheckboxInput from "@/components/global/CheckboxInput.vue";
import axios from "axios";

import dayjs from "dayjs";
require("dayjs/locale/de");
const isoWeek = require("dayjs/plugin/isoWeek");
dayjs.extend(isoWeek);

const titleStore = useTitleStore();

const errors = ref([]);
const paymentMethods = ref(null);
const unpayedOrders = ref(null);

const $toast = useToast();
const emitter = inject("emitter");

const currency = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
});

const getPaymentMethods = () => {
    errors.value = [];
    let tabElements = [];

    axios
        .get("payment_methods")
        .then((response) => {
            paymentMethods.value = [{id: 'all', name: "Alle"}, ...response.data];
            
            nextTick(() => {                
                paymentMethods.value.forEach((paymentMethod) => {
                    tabElements.push({
                        id: paymentMethod.id,
                        triggerEl: document.querySelector("#payment-tab-" + paymentMethod.id),
                        targetEl: document.querySelector("#payment-content-" + paymentMethod.id),
                    });
                });

                const options = {
                    activeClasses: "text-black border-b-4 border-purple",
                    inactiveClasses: "text-gray-light",
                    defaultTabId: "all"
                };
                new Tabs(tabElements, options);
            });
        })
        .catch((error) => {
            console.log(error);
            if (error.response.status === 404) {
                errors.value["paymentMethods"] = error.response.data.message;
                paymentMethods.value = [];
            } else {
                console.log(error);
            }
        });
};

const getOpenPayments = () => {
    errors.value = [];

    axios
        .get("orders/unpayed")
        .then((response) => {
            unpayedOrders.value = response.data;

            let accumulateByCustomerAndCalendarWeek = unpayedOrders.value.reduce((acc, order) => {
                const week = dayjs(order.date).isoWeek() + "/" + dayjs(order.date).isoWeekYear();
                const weekKey = String(dayjs(order.date).isoWeekYear()).substring(2) + "" + String(dayjs(order.date).isoWeek()).padStart(2, '0');

                if(!acc[order.customer.id]) {
                    acc[order.customer.id] = {};
                    acc[order.customer.id].customer_name = order.customer.name;
                    acc[order.customer.id].orders = [];
                }

                if (!acc[order.customer.id].orders[weekKey]) {
                    acc[order.customer.id].orders[weekKey] = {
                        ...order,
                        week: week,
                        ordermeals: [],
                        order_ids: [],
                    };
                }

                order.ordermeals.forEach((meal) => {
                    acc[order.customer.id].orders[weekKey].ordermeals.push(meal);
                });

                acc[order.customer.id].orders[weekKey].order_ids.push(order.id);

                return acc;
            }, []);
            
            let sorted = accumulateByCustomerAndCalendarWeek.sort((a, b) => {
                if (a.customer_name < b.customer_name) return -1;
                if (a.customer_name > b.customer_name) return 1;
                return 0;
            });

            sorted.forEach( customerGroup => {
                customerGroup.orders = customerGroup.orders.sort( (a, b) => {
                    return Date.parse(b.date) - Date.parse(a.date);
                });
            });

            unpayedOrders.value = sorted.flatMap(customerGroup => Object.values(customerGroup.orders))

// let sortedByName = Object.values(accumulateByCustomerAndCalendarWeek).sort( (a, b) => {
//         if (a.customer.name < b.customer.name) return -1;
//         if (a.customer.name > b.customer.name) return 1;
//         return 0;
//     } );

//             console.log(sortedByName);

//             unpayedOrders.value = sortedByName.flat();

//             let orderedByWeekDesc = Object.values(accumulateByCustomerAndCalendarWeek).sort((a, b) => {
//                 if (a.customer.name < b.customer.name) return -1;
//                 if (a.customer.name > b.customer.name) return 1;
//                 return b.date - a.date;
//             });

//             console.log(orderedByWeekDesc);

//             unpayedOrders.value = orderedByWeekDesc;
            
            getPaymentMethods();
        })
        .catch((error) => {
            console.log(error);
            if (error.response?.status === 404) {
                errors.value["unpayedOrders"] = error.response.data.message;
                unpayedOrders.value = [];
            } else {
                console.log(error);
            }
        });
};

onMounted(async () => {
    titleStore.setTitle("Offene Zahlungen");

    getOpenPayments();
});

const setOrdersPaymentStatus = async (order_ids, status) => {
    let saved_orders = [];

    try {
        saved_orders = (await axios.post("orders/set_payment_status", {order_ids: order_ids, payment_status: status})).data.orders;
        saved_orders.forEach((saved_order) => {
            emitter.emit("order-saved", saved_order);
        });
        $toast.success(saved_orders.length + " Zahlungen gespeichert!");
    } catch (error) {
        let error_string = "";
        if (error.response.data.error) {
            error_string = error.response.data.error;
        } else if (error.response.data.errors) {
            error_string = error.response.data.errors;
            return;
        } else {
            error_string = error;
        }
        $toast.error("Fehler: " + error_string + "<br>Zahlungen nicht gespeichert!");
    }
};

const printPage = () => {
    window.print();
};
</script>
